import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { Execution, ExecutionLogs, Task } from "../types";

type Tab = "overview" | "logs";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "logs", label: "Logs" },
];

export default function TaskDetailPage() {
  const { project } = useProject();
  const { call } = useApi();
  const { jiraIssueKey = "" } = useParams();
  const [task, setTask] = useState<Task | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [tab, setTab] = useState<Tab>("overview");
  const [queueing, setQueueing] = useState(false);
  const [queueError, setQueueError] = useState<string | undefined>();

  useEffect(() => {
    setTask(undefined);
    setError(undefined);
    call<Task>(`/api/projects/${project.projectId}/tasks/${encodeURIComponent(jiraIssueKey)}`)
      .then(setTask)
      .catch((e: Error) => setError(e.message));
  }, [call, project.projectId, jiraIssueKey]);

  const canQueue =
    task?.assigneeAgent &&
    !task.assigneeIsHuman &&
    (!task.currentExecutionStatus || ["NONE", "STOPPED", "FAILED"].includes(task.currentExecutionStatus));

  const handleQueue = useCallback(async () => {
    if (!task) return;
    setQueueing(true);
    setQueueError(undefined);
    try {
      await call(`/api/projects/${project.projectId}/tasks/${encodeURIComponent(task.jiraIssueKey)}/queue`, {
        method: "POST",
      });
      // Refresh task to reflect new status
      const updated = await call<Task>(
        `/api/projects/${project.projectId}/tasks/${encodeURIComponent(task.jiraIssueKey)}`
      );
      setTask(updated);
    } catch (e: unknown) {
      setQueueError((e as Error).message);
    } finally {
      setQueueing(false);
    }
  }, [call, project.projectId, task]);

  if (error) return <p className="error">{error}</p>;
  if (!task) return <p>Loading task…</p>;

  const jiraUrl = `${project.jiraBaseUrl.replace(/\/$/, "")}/browse/${task.jiraIssueKey}`;

  return (
    <>
      <div className="page-header">
        <p style={{ margin: 0 }}>
          <Link to={`/projects/${project.projectId}/tasks`}>← Tasks</Link>
        </p>
        <h1 style={{ marginBottom: 4 }}>
          {task.jiraIssueKey}
          {task.summary ? <span className="muted"> — {task.summary}</span> : null}
        </h1>
        <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 12 }}>
          <a href={jiraUrl} target="_blank" rel="noreferrer">
            Open in Jira ↗
          </a>
          <button
            disabled={!canQueue || queueing}
            onClick={handleQueue}
            title={!task.assigneeAgent ? "Task must be assigned to an agent" : undefined}
          >
            {queueing ? "Queueing…" : "Queue execution"}
          </button>
          {queueError && <span className="error" style={{ fontSize: "0.85em" }}>{queueError}</span>}
        </p>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`tab${tab === t.id ? " active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab task={task} />}
      {tab === "logs" && <LogsTab projectId={project.projectId} jiraIssueKey={task.jiraIssueKey} />}
    </>
  );
}

function OverviewTab({ task }: { task: Task }) {
  const totalTokens = (task.totalInputTokens ?? 0) + (task.totalOutputTokens ?? 0);
  return (
    <div className="card">
      <h2>Overview</h2>
      <dl className="kv-grid">
        <dt>Issue key</dt>
        <dd>{task.jiraIssueKey}</dd>
        <dt>Status</dt>
        <dd>{task.status ?? <span className="muted">—</span>}</dd>
        <dt>Type</dt>
        <dd>{task.isEpic ? "Epic" : "Issue"}</dd>
        <dt>Parent</dt>
        <dd>{task.parentIssueKey ?? <span className="muted">—</span>}</dd>
        <dt>Epic</dt>
        <dd>{task.epicIssueKey ?? <span className="muted">—</span>}</dd>
        <dt>Assignee</dt>
        <dd>
          {task.assigneeIsHuman ? (
            <span className="status-badge">Human</span>
          ) : (
            task.assigneeAgent ?? <span className="muted">Unassigned</span>
          )}
        </dd>
        <dt>Current execution</dt>
        <dd>
          {task.currentExecutionStatus && task.currentExecutionStatus !== "NONE" ? (
            <span className={`status-badge ${task.currentExecutionStatus}`}>
              {task.currentExecutionStatus}
            </span>
          ) : (
            <span className="muted">None</span>
          )}
        </dd>
        <dt>Executions</dt>
        <dd>{task.executionCount ?? 0}</dd>
        <dt>Tokens (input / output)</dt>
        <dd>
          {(task.totalInputTokens ?? 0).toLocaleString()} /{" "}
          {(task.totalOutputTokens ?? 0).toLocaleString()} ({totalTokens.toLocaleString()} total)
        </dd>
        <dt>Cost (USD)</dt>
        <dd>{(task.totalCostUSD ?? 0).toFixed(2)}</dd>
        <dt>Created</dt>
        <dd>{formatTimestamp(task.createdAt)}</dd>
        <dt>Updated</dt>
        <dd>{formatTimestamp(task.updatedAt)}</dd>
      </dl>
    </div>
  );
}

function LogsTab({ projectId, jiraIssueKey }: { projectId: string; jiraIssueKey: string }) {
  const { call } = useApi();
  const [executions, setExecutions] = useState<Execution[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [openId, setOpenId] = useState<string | undefined>();

  useEffect(() => {
    setExecutions(undefined);
    setError(undefined);
    call<Execution[]>(
      `/api/projects/${projectId}/tasks/${encodeURIComponent(jiraIssueKey)}/executions`,
    )
      .then((items) => {
        setExecutions(items);
        if (items.length > 0) setOpenId(items[0].executionId);
      })
      .catch((e: Error) => setError(e.message));
  }, [call, projectId, jiraIssueKey]);

  if (error) return <p className="error">{error}</p>;
  if (!executions) return <p>Loading executions…</p>;
  if (executions.length === 0) {
    return (
      <div className="card">
        <p className="muted">No agent executions have been launched for this task yet.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0 }}>
      <table>
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>Started</th>
            <th>Agent</th>
            <th>Status</th>
            <th>Exit</th>
            <th style={{ textAlign: "right" }}>Tokens</th>
            <th style={{ textAlign: "right" }}>Cost (USD)</th>
          </tr>
        </thead>
        <tbody>
          {executions.map((execution) => {
            const isOpen = openId === execution.executionId;
            return (
              <ExecutionRow
                key={execution.executionId}
                execution={execution}
                isOpen={isOpen}
                onToggle={() => setOpenId(isOpen ? undefined : execution.executionId)}
                projectId={projectId}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ExecutionRowProps {
  execution: Execution;
  isOpen: boolean;
  onToggle: () => void;
  projectId: string;
}

function ExecutionRow({ execution, isOpen, onToggle, projectId }: ExecutionRowProps) {
  const totalTokens = (execution.inputTokens ?? 0) + (execution.outputTokens ?? 0);
  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={onToggle}>
        <td>
          <button className="toggle" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
            {isOpen ? "▾" : "▸"}
          </button>
        </td>
        <td>{formatTimestamp(execution.startedAt ?? execution.createdAt)}</td>
        <td>{execution.agentName}</td>
        <td>
          <span className={`status-badge ${execution.status}`}>{execution.status}</span>
        </td>
        <td>{execution.exitCode ?? <span className="muted">—</span>}</td>
        <td style={{ textAlign: "right" }}>{totalTokens.toLocaleString()}</td>
        <td style={{ textAlign: "right" }}>{(execution.costUSD ?? 0).toFixed(2)}</td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={7} style={{ background: "var(--color-surface-alt)" }}>
            <ExecutionDetails execution={execution} projectId={projectId} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExecutionDetails({ execution, projectId }: { execution: Execution; projectId: string }) {
  const { call } = useApi();
  const [logs, setLogs] = useState<ExecutionLogs | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const load = useMemo(
    () => () => {
      setLoading(true);
      setError(undefined);
      call<ExecutionLogs>(
        `/api/projects/${projectId}/executions/${encodeURIComponent(execution.executionId)}/logs?limit=2000`,
      )
        .then(setLogs)
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [call, projectId, execution.executionId],
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ padding: "12px 16px" }}>
      <dl className="kv-grid" style={{ marginBottom: 12 }}>
        <dt>Execution ID</dt>
        <dd><code>{execution.executionId}</code></dd>
        <dt>ECS task</dt>
        <dd>{execution.ecsTaskId ? <code>{execution.ecsTaskId}</code> : <span className="muted">—</span>}</dd>
        <dt>Started</dt>
        <dd>{formatTimestamp(execution.startedAt)}</dd>
        <dt>Stopped</dt>
        <dd>{formatTimestamp(execution.stoppedAt)}</dd>
        {execution.stoppedReason && (
          <>
            <dt>Stopped reason</dt>
            <dd>{execution.stoppedReason}</dd>
          </>
        )}
      </dl>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>
          CloudWatch logs{logs?.logStreamName ? ` — ${logs.logStreamName}` : ""}
        </h3>
        <button onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {logs?.message && <p className="muted">{logs.message}</p>}
      {logs && logs.events.length > 0 && (
        <pre className="log-viewer">
          {logs.events
            .map((e) => `${formatTimestamp(e.timestamp)}  ${e.message ?? ""}`)
            .join("\n")}
        </pre>
      )}
      {logs && logs.events.length === 0 && !logs.message && (
        <p className="muted">No log events yet.</p>
      )}
    </div>
  );
}

function formatTimestamp(value: string | number | undefined): string {
  if (value === undefined || value === null || value === "") return "—";
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z");
}
